import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BadgeDollarSign, ChevronLeft, ChevronRight, ChevronDown, Loader2, Info,
} from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { useEphemeralState } from '../../lib/persist.js'
import { MONTHS_TH, MONTHS_TH_SHORT, nowYM } from '../../lib/timetable.js'
import {
  groupByMonth, totals, fmtSigned, amountTone, periodForMonth, isRecurringRow,
} from '../../lib/salary.js'

// พื้นที่ของฉัน → เงินเดือน. The staff member's OWN salary ledger, READ-ONLY.
//
// The record is `salary_em`: one row = one SIGNED line (amount > 0 earning,
// < 0 deduction), tagged with the {year, month} of the payroll run it belongs
// to. Net for a month = Σ amount. That is the whole model — this page adds no
// arithmetic of its own beyond the sums, so what it shows can never disagree
// with what HR's ledger (/manager/employee › เงินเดือน) shows.
//
// It is read-only by design, not by omission: /api/me/salary is a GET-only
// proxy and every write stays on the HR endpoints. A staff member reading
// their own payslip is self-service; a staff member editing it is not.
//
// Shape: a YEAR at a time, because "the salary detail of every month" is a
// comparison question — one month's net means little without the run before
// it. Months are cards, newest first, and the newest opens by itself since
// that is the one being asked about ~90% of the time.

// The year the ledger starts. Nothing in salary_em predates the clinic, and a
// stepper with no floor lets someone click into an empty decade.
const FIRST_YEAR = 2019

export default function MeSalary() {
  const current = nowYM()
  // Ephemeral: a reload returns to this year. The year you were browsing is a
  // scroll position, not a preference.
  const [year, setYear] = useEphemeralState('me:sal:year', current.year)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const reqRef = useRef(0)

  useEffect(() => {
    const seq = ++reqRef.current
    setLoading(true)
    setErr('')
    apiGet('/api/me/salary', { year })
      .then((r) => {
        if (seq !== reqRef.current) return
        setRows(Array.isArray(r?.items) ? r.items : [])
        setTotal(Number(r?.itemsTotal) || 0)
      })
      .catch((e) => {
        if (seq !== reqRef.current) return
        setRows([])
        setErr(readErr(e))
      })
      .finally(() => { if (seq === reqRef.current) setLoading(false) })
  }, [year])

  const months = useMemo(() => groupByMonth(rows), [rows])
  // Summed from the GROUPED rows, not the raw ones: groupByMonth drops a row
  // that carries no {year, month} (it cannot be filed under a month), and a
  // year total that counted a line no card shows would be unreconcilable.
  const sum = useMemo(() => totals(months.flatMap((g) => g.rows)), [months])
  // `sum.earn` / `sum.deduct` are intentionally left unread — see the strip below.

  // The newest month of the year in view opens by itself; the rest are closed.
  // `null` = untouched (follow that default); '' = the user closed it.
  const [openKey, setOpenKey] = useState(null)
  useEffect(() => { setOpenKey(null) }, [year]) // a new year re-arms the default
  const newestKey = months.length ? keyOf(months[0]) : null
  const isOpen = (g) => (openKey === null ? keyOf(g) === newestKey : openKey === keyOf(g))

  return (
    <div className="max-w-[820px]">
      {/* Year + the year's three totals. Earnings/deductions/net in that order:
          it reads the way the slip is built. */}
      <section className="rounded-2xl border border-slate-200/80 bg-white">
        <header className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100">
          <BadgeDollarSign size={15} className="text-brand-600 flex-none" strokeWidth={1.9} />
          <h2 className="text-[14px] font-semibold text-slate-800 whitespace-nowrap">เงินเดือนของฉัน</h2>
          <div className="ml-auto flex items-center gap-1 flex-none">
            <StepBtn onClick={() => setYear((y) => y - 1)} disabled={year <= FIRST_YEAR} title="ปีก่อนหน้า">
              <ChevronLeft size={15} strokeWidth={2} />
            </StepBtn>
            <span className="text-[13px] font-medium text-slate-700 tabular-nums px-1.5 whitespace-nowrap">
              {year + 543}
            </span>
            <StepBtn onClick={() => setYear((y) => y + 1)} disabled={year >= current.year} title="ปีถัดไป">
              <ChevronRight size={15} strokeWidth={2} />
            </StepBtn>
          </div>
        </header>

        {/* Net and month count only. There is deliberately NO deduction total
            and no gross beside the net: an aggregate of what was taken off you
            is the first thing the eye lands on, and it makes a payslip read as
            an accusation. The individual lines below still carry every real
            figure — each one small, specific and explainable, which a big red
            sum is not. */}
        <div className="grid grid-cols-2 divide-x divide-slate-100">
          <Stat label={`สุทธิทั้งปี ${year + 543}`} value={money(sum.net)} />
          <Stat label="เดือนที่มีรายการ" value={months.length || '—'} />
        </div>
      </section>

      {loading && (
        <div className="flex items-center gap-2 text-[13px] text-slate-400 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && err && (
        <p className="text-[13px] text-rose-600 bg-rose-50 ring-1 ring-rose-200 rounded-xl px-3.5 py-2.5 mt-4">{err}</p>
      )}

      {!loading && !err && !months.length && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center mt-4">
          <div className="text-[14px] text-slate-600">ยังไม่มีรายการเงินเดือนในปี {year + 543}</div>
          <p className="text-[12.5px] text-slate-400 mt-1">
            รายการจะขึ้นเมื่อฝ่ายบุคคลปิดรอบเงินเดือนของเดือนนั้นแล้ว
          </p>
        </div>
      )}

      {!loading && !err && months.map((g) => (
        <MonthCard
          key={keyOf(g)}
          group={g}
          open={isOpen(g)}
          onToggle={() => setOpenKey(isOpen(g) ? '' : keyOf(g))}
        />
      ))}

      {/* One page holds a whole year in practice (the proxy asks for 500 rows).
          Say so out loud if it ever doesn't — a truncated list reads as
          "this is everything I was paid". */}
      {!loading && !err && total > rows.length && (
        <p className="text-[11.5px] text-amber-600 pt-3">
          แสดง {rows.length} จาก {total} รายการในปีนี้ — ดูครบได้ที่ฝ่ายบุคคล
        </p>
      )}

      {!loading && !err && !!months.length && (
        <p className="flex items-start gap-1.5 text-[11.5px] text-slate-400 mt-4 leading-relaxed">
          <Info size={12} className="mt-[2px] flex-none" strokeWidth={1.9} />
          <span>
            หน้านี้แสดงข้อมูลอย่างเดียว แก้ไขไม่ได้ — หากตัวเลขไม่ตรง กรุณาแจ้งฝ่ายบุคคล
          </span>
        </p>
      )}
    </div>
  )
}

const keyOf = (g) => `${g.year}-${g.month}`

/* ── one month ─────────────────────────────────────────────────────────────── */

// Collapsed, a month is its net — the number the question is actually about.
// Expanded, it is every line that produced it, in the order the slip is built:
// earnings first, largest first, then the deductions.
function MonthCard({ group, open, onToggle }) {
  const { year, month, rows, net } = group
  const period = periodForMonth(year, month)

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white mt-4 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-slate-50/70 transition-colors"
      >
        <ChevronDown
          size={15}
          strokeWidth={2}
          className={`text-slate-300 flex-none transition-transform ${open ? '' : '-rotate-90'}`}
        />
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-slate-800 whitespace-nowrap">
            {MONTHS_TH[month - 1]} {year + 543}
          </div>
          {/* The run this month's tag means. gen2 writes {year, month} from the
              END of a 26→25 window, so the month name alone is ambiguous to
              anyone counting their own shifts. */}
          <div className="text-[11.5px] text-slate-400 whitespace-nowrap">
            รอบ {fmtDayMon(period.start)} – {fmtDayMon(period.end)} · {rows.length} รายการ
          </div>
        </div>
        {/* The net, and nothing else. The collapsed card used to break it into
            "+earnings −deductions"; that made the row you skim past a running
            tally of what you lost. Open the card to see where it went. */}
        <div className="ml-auto text-right flex-none">
          <div className={`text-[17px] font-semibold tabular-nums ${amountTone(net)}`}>{money(net)}</div>
          <div className="text-[11px] text-slate-400 whitespace-nowrap">สุทธิ</div>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 sm:px-5 py-1">
          {rows.map((r) => <Line key={r.id} row={r} />)}
          <div className="flex items-center gap-3 py-3 border-t border-slate-200/70 mt-1">
            <span className="text-[13px] font-semibold text-slate-700">สุทธิ</span>
            <span className={`ml-auto text-[15px] font-semibold tabular-nums ${amountTone(net)}`}>
              {money(net)}
            </span>
          </div>
        </div>
      )}
    </section>
  )
}

// A single signed line. The sign of `amount` decides the colour — never the
// type: หักสาย with a positive amount is a correction HR made on purpose, and
// painting it red because of its name would misreport it.
//
// Deliberately says NOTHING about `generate` — whether a line was calculated by
// gen2 or keyed by hand is HR's own workflow, and surfacing it here would only
// invite "why is this one different?" about a distinction the employee has no
// use for. The proxy does not even send the field.
function Line({ row }) {
  const amount = Number(row?.amount || 0)
  const recurring = isRecurringRow(row)
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0">
      <div className="min-w-0">
        <div className="text-[13px] text-slate-700 flex items-center gap-1.5 flex-wrap">
          <span>{row.type || 'อื่นๆ'}</span>
          {recurring && (
            <span className="text-[10.5px] px-1.5 py-[1px] rounded-full ring-1 bg-indigo-50 text-indigo-700 ring-indigo-200 whitespace-nowrap">
              ผ่อนชำระ
            </span>
          )}
        </div>
        {row.remark && (
          <div className="text-[12px] text-slate-400 mt-0.5 break-words">{row.remark}</div>
        )}
      </div>
      <div className={`ml-auto flex-none text-[13.5px] font-medium tabular-nums ${amountTone(amount)}`}>
        {fmtSigned(amount)}
      </div>
    </div>
  )
}

/* ── bits ──────────────────────────────────────────────────────────────────── */

function StepBtn({ children, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  )
}

function Stat({ label, value, tone = '' }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[11.5px] text-slate-400 whitespace-nowrap">{label}</div>
      <div className={`text-[17px] font-semibold tabular-nums ${tone || 'text-slate-800'}`}>{value}</div>
    </div>
  )
}

// fmtMoney() prints 0 as "—", which is right for an optional field and wrong
// for a total: a month whose net is 0 was still a payroll run, and "—" reads as
// "not known". Negatives use U+2212, matching fmtSigned and the rest of the app.
function money(n) {
  const v = Number(n) || 0
  if (v === 0) return '0'
  const body = Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
  return v < 0 ? `\u2212${body}` : body
}

// "26 ก.ค." from an ISO date — the payroll window is read as two day-month
// marks, so the year would only be noise (it is on the card already).
function fmtDayMon(isoDate) {
  const m = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return '—'
  return `${Number(m[3])} ${MONTHS_TH_SHORT[Number(m[2]) - 1] || ''}`
}

function readErr(e) {
  const s = String(e?.message || e || '')
  if (e?.status === 403 || /no_employee_record/.test(s)) {
    return 'บัญชีนี้ยังไม่ได้ผูกกับข้อมูลพนักงาน — กรุณาติดต่อฝ่ายบุคคล'
  }
  return 'โหลดข้อมูลเงินเดือนไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
}
